import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { StyledAvatar } from '../../../components/atoms/Avatar';
import { Resident } from '../../../components/molecules/CascadedAvatars';
import defaultAvatar from 'images/avatar.svg';

function sortByAvatarPresence(a: Resident, b: Resident): number {
  if (a.image_url && !b.image_url) return -1;
  if (!a.image_url && b.image_url) return 1;
  return 0;
}

const Stack = styled.ul`
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const StackItem = styled.li<{
  $isOverflow?: boolean;
  $zIndex?: number;
}>`
  position: relative;
  z-index: ${({ $zIndex }) => $zIndex ?? 1};
  margin-left: -0.65rem;
  transition: margin-left 160ms ease;

  cursor: pointer;
  &:first-child {
    margin-left: 0;
  }

  ${({ $isOverflow }) =>
    $isOverflow &&
    css`
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f5f5f4;
      border-radius: 50%;
      width: 2rem;
      height: 2rem;
      color: #57534e;
      font-weight: 600;
      font-size: 0.875rem;
      box-shadow: 0 0 0 2px #ffffff;

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 50%;
        border: 1px solid rgba(214, 211, 209, 0.35);
        pointer-events: none;
      }
    `}
`;

const AvatarWrapper = styled.span`
  position: relative;
  display: block;
  border-radius: 50%;
  background: #fff;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1px solid rgba(214, 211, 209, 0.35);
    pointer-events: none;
  }
`;

const SizedAvatar = styled(StyledAvatar)`
  display: block;
  border: none;
  box-shadow: 0 0 0 2px #ffffff;

  &:hover {
    border: none;
    outline: none;
  }
`;

export function StackedAvatars({
  residents,
  limit = 3,
  totalCount,
  position = 'right',
}: Readonly<{
  residents: Resident[];
  limit?: number;
  totalCount?: number;
  position?: 'left' | 'right';
}>) {
  const visible = useMemo(
    () => [...(residents || [])].sort(sortByAvatarPresence).slice(0, limit),
    [residents, limit]
  );

  if (!visible.length) {
    return null;
  }

  const isLeft = position === 'left';
  const total = totalCount ?? residents.length;
  const remaining = Math.max(total - visible.length, 0);
  const hasOverflow = remaining > 0;
  const itemCount = visible.length + (hasOverflow ? 1 : 0);

  const avatarZIndex = (i: number) => (isLeft ? itemCount - 1 - i : i + 1);

  const overflowChip = hasOverflow ? (
    <StackItem $isOverflow $zIndex={itemCount}>
      +{remaining}
    </StackItem>
  ) : null;

  return (
    <Stack>
      {isLeft && overflowChip}
      {visible.map((resident, i) => (
        <StackItem key={resident.id} $zIndex={avatarZIndex(isLeft ? i + 1 : i)}>
          <AvatarWrapper>
            <SizedAvatar
              alt={resident.first_name}
              title={resident.first_name}
              round
              src={resident.image_url || defaultAvatar}
              size={2}
            />
          </AvatarWrapper>
        </StackItem>
      ))}
      {!isLeft && overflowChip}
    </Stack>
  );
}
